import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Download, MoreHorizontal } from 'lucide-react';
import { useLeads } from '@/composables/crm/useLeads';
import { useExport } from '@/composables/shared/useExport';
import { useDebounce } from '@/composables/shared/useDebounce';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { formatDate, formatFullName } from '@/lib/formatters';
import { useState, useMemo } from 'react';
import type { Lead } from '@/types/table';

export function LeadListPage() {
  const navigate = useNavigate();
  const { leads, meta, isLoading, filters, exportCsv, deleteLead, convertLead } = useLeads();
  const { exportFile, isExporting } = useExport();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);

  const [deleteTarget, setDeleteTarget] = useState<Lead | null>(null);
  const [convertTarget, setConvertTarget] = useState<Lead | null>(null);
  const [deleteDeal, setDeleteDeal] = useState(false);

  if (debouncedSearch !== filters.current.search) {
    filters.set({ search: debouncedSearch });
  }

  const columns: ColumnDef<Lead, unknown>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      cell: ({ row }) => (
        <p className="font-medium">{formatFullName(row.original.firstName, row.original.lastName)}</p>
      ),
    },
    {
      accessorKey: 'email',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
      cell: ({ row }) => <span className="text-sm">{row.original.email || '-'}</span>,
    },
    {
      accessorKey: 'phone',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Phone" />,
      cell: ({ row }) => <span className="text-sm">{row.original.phone || '-'}</span>,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => <StatusBadge status={row.original.status} type="lead" />,
    },
    {
      accessorKey: 'source',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Source" />,
      cell: ({ row }) => row.original.source.replace('_', ' '),
    },
    {
      accessorKey: 'score',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Score" />,
      cell: ({ row }) => <span className="font-medium">{row.original.score}</span>,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/leads/${row.original.id}/edit`); }}>Edit</DropdownMenuItem>
            {row.original.status !== 'CONVERTED' && (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setConvertTarget(row.original); }}>Convert to Contact</DropdownMenuItem>
            )}
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer
      title="Leads"
      description="Manage and track your leads"
      actions={
        <>
          <Button variant="outline" onClick={() => exportFile(exportCsv, 'leads.csv')} disabled={isExporting}>
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
          <Button onClick={() => navigate('/leads/new')}>
            <Plus className="mr-2 h-4 w-4" /> Add Lead
          </Button>
        </>
      }
    >
      <DataTableToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search leads..."
      />
      <DataTable
        columns={columns}
        data={leads}
        isLoading={isLoading}
        page={meta?.page ?? 1}
        totalPages={meta?.totalPages ?? 1}
        total={meta?.total ?? 0}
        pageSize={filters.current.limit}
        onPageChange={(page) => filters.set({ page })}
        onPageSizeChange={(limit) => filters.set({ limit })}
        onRowClick={(row) => navigate(`/leads/${row.id}`)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => { setDeleteTarget(null); setDeleteDeal(false); }}
        title="Delete Lead"
        description={`Are you sure you want to delete ${deleteTarget ? formatFullName(deleteTarget.firstName, deleteTarget.lastName) : ''}? This action cannot be undone.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => {
          if (deleteTarget) {
            deleteLead.mutate({ id: deleteTarget.id, deleteDeal });
            setDeleteTarget(null);
            setDeleteDeal(false);
          }
        }}
        isLoading={deleteLead.isPending}
      >
        <div className="flex items-center space-x-2 py-2">
          <Checkbox id="deleteDeal" checked={deleteDeal} onCheckedChange={(checked) => setDeleteDeal(!!checked)} />
          <Label htmlFor="deleteDeal" className="text-sm font-normal">Also delete associated deal</Label>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!convertTarget}
        onOpenChange={() => setConvertTarget(null)}
        title="Convert Lead to Contact"
        description={`Convert ${convertTarget ? formatFullName(convertTarget.firstName, convertTarget.lastName) : ''} to a contact? A deal will also be created automatically.`}
        confirmLabel="Convert"
        onConfirm={() => {
          if (convertTarget) {
            convertLead.mutate({ id: convertTarget.id });
            setConvertTarget(null);
          }
        }}
        isLoading={convertLead.isPending}
      />
    </PageContainer>
  );
}
